import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';

export default function RateLimits() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/billing/plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      // Fallback data
      setPlans([
        { id: 'free', name: 'Free', quota: 100 },
        { id: 'starter', name: 'Starter', quota: 2000 },
        { id: 'growth', name: 'Growth', quota: 10000 },
        { id: 'pro', name: 'Pro', quota: 50000 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="ratelimits" className="mb-12">
        <h2 className="text-2xl font-bold mb-4">Rate Limits</h2>
        <p className="text-gray-600">Loading rate limits...</p>
      </section>
    );
  }

  return (
    <section id="ratelimits" className="mb-12">
      <h2 className="text-2xl font-bold mb-4">Rate Limits</h2>
      
      <p className="text-gray-600 mb-6">
        Snaptura enforces rate limits based on your subscription plan. Monitor your usage through the dashboard
        or via response headers.
      </p>

      <div className="overflow-x-auto mb-6">
        <table className="min-w-full border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                Plan
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 border-b">
                Monthly Quota
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {plans.map((plan) => (
              <tr key={plan.id}>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  {plan.name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {plan.quota.toLocaleString()} requests
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="text-xl font-semibold mb-3">Rate Limit Headers</h3>
      <p className="text-gray-600 mb-4">
        Every API response includes rate limit information in the headers:
      </p>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 font-mono text-sm mb-6">
        <div className="text-gray-700">
          <span className="text-blue-600">X-RateLimit-Limit:</span> 1000
        </div>
        <div className="text-gray-700">
          <span className="text-blue-600">X-RateLimit-Remaining:</span> 847
        </div>
        <div className="text-gray-700">
          <span className="text-blue-600">X-RateLimit-Used:</span> 153
        </div>
        <div className="text-gray-700">
          <span className="text-blue-600">X-RateLimit-Reset:</span> 2025-11-01T00:00:00.000Z
        </div>
        <div className="text-gray-700">
          <span className="text-blue-600">X-RateLimit-Plan:</span> starter
        </div>
      </div>

      <h3 className="text-xl font-semibold mb-3">Handling Rate Limits</h3>
      <p className="text-gray-600 mb-4">
        When you exceed your quota, you'll receive a <code className="bg-gray-100 px-2 py-1 rounded">429 Too Many Requests</code> error:
      </p>

      <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm mb-6 overflow-x-auto">
        <pre>{`{
  "error": "Quota exceeded",
  "message": "You have reached your monthly limit of 1000 requests",
  "usage": {
    "used": 1000,
    "limit": 1000,
    "remaining": 0,
    "plan": "starter"
  }
}`}</pre>
      </div>

      <p className="text-gray-600">
        Your quota resets on the first day of each month. Consider upgrading your plan if you consistently hit your limits.
      </p>
    </section>
  );
}